import Papa from "papaparse";
import { camelCase } from "camel-case";

/**
 * Reads a CSV file and returns data as an array with TypeScript typings.
 *
 * @param relativeUri Path to CSV file. Start with slash to make it relative to web root.
 * @param dynamicTyping When enabled will convert string of numbers to number type. Example { age: "12" } will become { age: 12 }.
 */
export function csvReader<TModel>(relativeUri: string, dynamicTyping: boolean = true): Promise<TModel[]> {
    return fetch(relativeUri)
        .then(res => res.text())
        .then(csvText => parseCsv(csvText, dynamicTyping));
}

export function parseCsv<TModel>(csvText: string, dynamicTyping: boolean = true): TModel[] {
    const parseResult = Papa.parse(csvText, {
        header: true, // if the csv file has a header row
        delimiter: ",", // force delimiter to ","
        skipEmptyLines: true,
        comments: "#", // enables comment support in csv file by prefixing row with "#"
        transformHeader: header => camelCase(header), // convert field names to camelcase
        transform: (value: string, field: string | number) => (value === "NULL" ? null : value), // replaces column value of "NULL" with actual `null`
        dynamicTyping: dynamicTyping
    });

    if (parseResult.errors?.length === 0) {
        return parseResult.data as TModel[];
    } else {
        console.warn("CSV parsing error. Parse result:", parseResult);

        throw new Error(
            "Failed to read CSV file with errors: \n" + parseResult.errors.map(err => "- " + err.message).join("\n")
        );
    }
}
